import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';
import { ClubConteoSocios, Plan, SuscripcionEstado, PlanSuscripcion, AuditEvento } from '../types';
import Modal from '../components/Modal';

const SuscripcionBadge: React.FC<{ status: SuscripcionEstado }> = ({ status }) => {
    const styles: Record<SuscripcionEstado, string> = {
        trialing: 'bg-indigo-200 text-indigo-800',
        active: 'bg-green-200 text-green-800',
        past_due: 'bg-red-200 text-red-800',
        canceled: 'bg-gray-200 text-gray-800',
    };
    const labels: Record<SuscripcionEstado, string> = {
        trialing: 'Prueba',
        active: 'Activa',
        past_due: 'Impaga',
        canceled: 'Cancelada',
    };
    return <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${styles[status]}`}>{labels[status]}</span>;
}

const UsageProgressBar: React.FC<{ count: number; limit: number }> = ({ count, limit }) => {
    if (limit === -1) {
        return <div className="text-sm text-gray-600">{count} / &infin;</div>;
    }
    const percentage = limit > 0 ? (count / limit) * 100 : 0;

    let barColor = 'bg-blue-500';
    if (percentage > 90) barColor = 'bg-red-500';
    else if (percentage > 75) barColor = 'bg-yellow-500';

    return (
        <div className="w-full">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>{count} / {limit}</span>
                <span>{percentage.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className={`${barColor} h-2.5 rounded-full`} style={{ width: `${Math.min(percentage, 100)}%` }}></div>
            </div>
        </div>
    );
};

const GestionarClubModal: React.FC<{
    club: ClubConteoSocios;
    planes: PlanSuscripcion[];
    onClose: () => void;
    onSave: (newPlan: Plan) => Promise<void>;
}> = ({ club, planes, onClose, onSave }) => {
    const [selectedPlan, setSelectedPlan] = useState<Plan>(club.plan);
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        await onSave(selectedPlan);
        setIsSaving(false);
    };

    const isChanged = selectedPlan !== club.plan;
    const activePlanes = planes.filter(p => p.activo);

    return (
        <Modal isOpen={true} onClose={onClose} title={`Gestionar Club: ${club.nombre}`}>
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-medium text-gray-900">Cambiar Plan</h3>
                    <p className="text-sm text-gray-500">Plan Actual: <span className="font-semibold">{club.plan}</span></p>
                </div>
                <fieldset className="space-y-4">
                    <legend className="sr-only">Planes</legend>
                    {activePlanes.map(plan => (
                        <div key={plan.id} className="relative flex items-start">
                            <div className="flex h-6 items-center">
                                <input
                                    id={`plan-${plan.nombre}`}
                                    name="plan"
                                    type="radio"
                                    checked={selectedPlan === plan.nombre}
                                    onChange={() => setSelectedPlan(plan.nombre)}
                                    className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600"
                                />
                            </div>
                            <div className="ml-3 text-sm leading-6">
                                <label htmlFor={`plan-${plan.nombre}`} className="font-medium text-gray-900">{plan.nombre}</label>
                                <p className="text-gray-500">{plan.descripcion}</p>
                            </div>
                        </div>
                    ))}
                </fieldset>

                <div className="border-t pt-4">
                    <h3 className="text-lg font-medium text-gray-900">Suscripción y Facturación</h3>
                    <div className="mt-2 text-sm text-gray-600">
                        <p>Estado: <SuscripcionBadge status={club.suscripcion_estado} /></p>
                        <p>ID Suscripción: <span className="font-mono bg-gray-100 px-1 rounded">{club.suscripcion_id || 'N/A'}</span></p>
                        <p>Próximo cobro: {club.periodo_actual_fin ? new Date(club.periodo_actual_fin).toLocaleDateString('es-AR') : 'N/A'}</p>
                    </div>
                    <button className="mt-4 text-sm text-blue-600 hover:underline">Gestionar Facturación en Mercado Pago</button>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button type="button" onClick={onClose} className="bg-white py-2 px-4 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 border border-gray-300">
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!isChanged || isSaving}
                        className="inline-flex justify-center rounded-md bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        {isSaving ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

const PlanFormModal: React.FC<{
    plan: Partial<PlanSuscripcion> | null;
    onClose: () => void;
    onSave: (plan: PlanSuscripcion | Omit<PlanSuscripcion, 'id' | 'creado_en'>) => Promise<boolean>;
}> = ({ plan, onClose, onSave }) => {
    const [formData, setFormData] = useState<Partial<PlanSuscripcion>>(plan || { activo: true, nombre: '', descripcion: '', precio: 0, intervalo: 'mes', limite_socios: 0 });
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const isNumber = type === 'number';
        setFormData(prev => ({ ...prev, [name]: isNumber ? parseFloat(value) : value }));
    };

    const ui = useUI();

    const handleSave = async () => {
        if (!formData.nombre) {
            ui.showModal('Error', 'El nombre del plan es obligatorio.');
            return;
        }
        setIsSaving(true);
        const success = await onSave(formData as any);
        setIsSaving(false);
        if (success) {
            onClose();
        }
    }

    return (
        <Modal isOpen={true} onClose={onClose} title={plan?.id ? 'Editar Plan' : 'Nuevo Plan'}>
            <div className="space-y-4">
                <input type="text" name="nombre" placeholder="Nombre del Plan" value={formData.nombre || ''} onChange={handleChange} className="w-full p-2 border rounded" />
                <textarea name="descripcion" placeholder="Descripción breve" value={formData.descripcion || ''} onChange={handleChange} className="w-full p-2 border rounded" />
                <div className="grid grid-cols-2 gap-4">
                    <input type="number" name="precio" placeholder="Precio" value={formData.precio || ''} onChange={handleChange} className="w-full p-2 border rounded" />
                    <select name="intervalo" value={formData.intervalo || 'mes'} onChange={handleChange} className="w-full p-2 border rounded">
                        <option value="mes">Mensual</option>
                        <option value="año">Anual</option>
                    </select>
                </div>
                <input type="number" name="limite_socios" placeholder="Límite de socios (-1 para ilimitado)" value={formData.limite_socios || ''} onChange={handleChange} className="w-full p-2 border rounded" />
                <input type="text" name="mercadopago_plan_id" placeholder="ID de Plan de Mercado Pago (opcional)" value={formData.mercadopago_plan_id || ''} onChange={handleChange} className="w-full p-2 border rounded" />
                <div className="flex items-center">
                    <input type="checkbox" id="activo" name="activo" checked={formData.activo} onChange={e => setFormData(prev => ({ ...prev, activo: e.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600" />
                    <label htmlFor="activo" className="ml-2 block text-sm text-gray-900">Plan activo</label>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button onClick={onClose} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300">Cancelar</button>
                    <button onClick={handleSave} disabled={isSaving} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                        {isSaving ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </div>
        </Modal>
    )
}

const getIconForEventType = (type: string): React.ReactElement => {
    switch (type) {
        case 'NUEVO_CLUB':
            return <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21v-2a6 6 0 00-12 0v2" /></svg>;
        case 'PAGO_EXITOSO':
            return <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>;
        case 'CAMBIO_DE_PLAN_MANUAL':
            return <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>;
        default:
            return <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    }
};

const SuperAdminScreen: React.FC = () => {
    const { user, clubes, logout, updateClubPlan, planes, addPlan, updatePlan, auditLogs } = useContext(AppContext);
    const ui = useUI();
    const [manageClubModal, setManageClubModal] = useState<ClubConteoSocios | null>(null);
    const [planFormModal, setPlanFormModal] = useState<Partial<PlanSuscripcion> | null>(null);
    const [activeTab, setActiveTab] = useState<'clubes' | 'planes' | 'logs'>('clubes');

    const handleSaveClubPlan = async (newPlan: Plan) => {
        if (manageClubModal) {
            await updateClubPlan(manageClubModal.id, newPlan);
            setManageClubModal(null);
        }
    };

    const handleSavePlan = (planData: PlanSuscripcion | Omit<PlanSuscripcion, 'id' | 'creado_en'>): Promise<boolean> => {
        if ('id' in planData) {
            return updatePlan(planData);
        } else {
            return addPlan(planData);
        }
    };

    const togglePlanStatus = (plan: PlanSuscripcion) => {
        ui.showModal('Confirmar', `¿Seguro que querés ${plan.activo ? 'desactivar' : 'activar'} el plan "${plan.nombre}"?`, () => {
            updatePlan({ ...plan, activo: !plan.activo });
        });
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Panel de SuperAdmin</h1>
                        <p className="text-sm text-gray-500">Bienvenido, {user?.nombre}</p>
                    </div>
                    <button onClick={logout} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm font-medium">Cerrar Sesión</button>
                </div>
            </header>
            <main className="py-8">
                <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
                    <div className="px-4 sm:px-0">
                        <div className="border-b border-gray-200">
                            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                                <button onClick={() => setActiveTab('clubes')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'clubes' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Clubes</button>
                                <button onClick={() => setActiveTab('planes')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'planes' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Planes y Facturación</button>
                                <button onClick={() => setActiveTab('logs')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'logs' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Registro de Actividad</button>
                            </nav>
                        </div>
                    </div>

                    <div className="mt-8">
                        {activeTab === 'clubes' && (
                            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                                <div className="p-6">
                                    <h2 className="text-xl font-semibold text-gray-800">Clubes Registrados ({clubes.length})</h2>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Club</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dominio / Admin</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-48">Uso (Socios)</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Suscripción</th>
                                                <th scope="col" className="relative px-6 py-3"><span className="sr-only">Acciones</span></th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {clubes.map(club => {
                                                const planDetails = planes.find(p => p.nombre === club.plan);
                                                const limit = planDetails ? planDetails.limite_socios : 0;
                                                return (
                                                    <tr key={club.id}>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <p className="text-sm font-medium text-gray-900">{club.nombre}</p>
                                                            <p className="text-xs text-gray-500">Reg: {new Date(club.creado_en).toLocaleDateString('es-AR')}</p>
                                                            {club.modulos && club.modulos.length > 0 && (
                                                                <div className="mt-1 flex gap-1 flex-wrap">
                                                                    {club.modulos.map(m => (
                                                                        <span key={m} className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-800 rounded">{m}</span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <p className="text-sm text-gray-900">{club.dominio || '-'}</p>
                                                            <p className="text-xs text-gray-500">{club.subdominio ? `${club.subdominio}.tuclub.com` : '-'}</p>
                                                            <div className="mt-1 pt-1 border-t border-gray-100">
                                                                <p className="text-xs font-medium text-gray-700">{club.admin_nombre || 'Sin Admin'}</p>
                                                                <p className="text-xs text-gray-500">{club.admin_email || ''}</p>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{club.plan}</span></td>
                                                        <td className="px-6 py-4 whitespace-nowrap"><UsageProgressBar count={club.socios_conteo} limit={limit} /></td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <SuscripcionBadge status={club.suscripcion_estado} />
                                                            <p className="text-xs text-gray-500 mt-1">Prox: {club.periodo_actual_fin ? new Date(club.periodo_actual_fin).toLocaleDateString('es-AR') : '-'}</p>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"><button onClick={() => setManageClubModal(club)} className="text-indigo-600 hover:text-indigo-900">Gestionar</button></td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {activeTab === 'planes' && (
                            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                                <div className="p-6 flex justify-between items-center">
                                    <h2 className="text-xl font-semibold text-gray-800">Planes de Suscripción</h2>
                                    <button onClick={() => setPlanFormModal({})} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">Nuevo Plan</button>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Límite Socios</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                                                <th scope="col" className="relative px-6 py-3"><span className="sr-only">Acciones</span></th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {planes.map(plan => (
                                                <tr key={plan.id}>
                                                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">{plan.nombre}</div><div className="text-xs text-gray-500">{plan.descripcion}</div></td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">${plan.precio.toLocaleString('es-AR')} / {plan.intervalo}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{plan.limite_socios === -1 ? 'Ilimitados' : plan.limite_socios}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${plan.activo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{plan.activo ? 'Activo' : 'Inactivo'}</span></td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                                                        <button onClick={() => setPlanFormModal(plan)} className="text-yellow-600 hover:text-yellow-900">Editar</button>
                                                        <button onClick={() => togglePlanStatus(plan)} className={plan.activo ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}>{plan.activo ? 'Desactivar' : 'Activar'}</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {activeTab === 'logs' && (
                            <div className="bg-white rounded-lg shadow-md">
                                <div className="p-6">
                                    <h2 className="text-xl font-semibold text-gray-800">Registro de Actividad de la Plataforma</h2>
                                </div>
                                <div className="flow-root">
                                    <ul role="list" className="p-4 sm:p-6 -mb-8">
                                        {auditLogs.map((log, logIdx) => (
                                            <li key={log.id}>
                                                <div className="relative pb-8">
                                                    {logIdx !== auditLogs.length - 1 ? (
                                                        <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                                                    ) : null}
                                                    <div className="relative flex space-x-3">
                                                        <div>
                                                            <span className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center ring-8 ring-white">
                                                                {getIconForEventType(log.tipo_evento)}
                                                            </span>
                                                        </div>
                                                        <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                                                            <div>
                                                                <p className="text-sm text-gray-700">{log.descripcion}</p>
                                                            </div>
                                                            <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                                                <time dateTime={log.creado_en}>{new Date(log.creado_en).toLocaleString('es-AR')}</time>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                        {auditLogs.length === 0 && (
                                            <li className="text-center text-gray-500 py-8">No hay eventos registrados.</li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
            {manageClubModal && (
                <GestionarClubModal
                    club={manageClubModal}
                    planes={planes}
                    onClose={() => setManageClubModal(null)}
                    onSave={handleSaveClubPlan}
                />
            )}
            {planFormModal && (
                <PlanFormModal
                    plan={planFormModal.id ? planFormModal : null}
                    onClose={() => setPlanFormModal(null)}
                    onSave={handleSavePlan}
                />
            )}
        </div>
    );
};

export default SuperAdminScreen;